// Progression: experience, levels, unlocks and achievements.
import { fmtTime } from './race.js';

/** Facts about an activity used for XP and achievements. */
export type ActivityType = 'race' | 'workout' | 'game';
export interface ActivityFacts {
  type: ActivityType; mode?: string | null; rank?: number | null; hits?: number; online?: boolean; completed?: boolean; test?: string | null;
  verified?: boolean; ghostRecord?: boolean; time?: number | null; distance: number; duration: number; TSS?: number; maxPower?: number;
}
export interface Totals { rides: number; km: number; hours: number; elevation: number; wins: number; hits: number; workouts: number; tests: number }
/** `challenges`: legendary challenges completed by this activity. */
export interface AchievementCtx { act: ActivityFacts; totals: Totals; streak: number; profile: { mass: number }; planCompleted?: boolean; challenges?: string[] }
export interface Achievement { id: string; icon: string; name: string; desc: string; xp: number; test: (c: AchievementCtx) => boolean }
export interface Unlock { level: number; kind: 'look' | 'track' | 'mood' | 'difficulty'; slot?: 'pattern' | 'frame' | 'helmet'; id: string; name: string }
export interface LevelInfo { level: number; xp: number; cur: number; next: number; frac: number }
export type LookSlot = 'pattern' | 'frame' | 'helmet';


export const MAX_LEVEL = 50;
/** Cumulative XP needed to reach level L. */
export const xpForLevel = (L: number): number => Math.round(150 * Math.pow(Math.max(0, L - 1), 1.7));
export function levelFromXp(xp: number): LevelInfo {
  let L = 1;
  while (L < MAX_LEVEL && xp >= xpForLevel(L + 1)) L++;
  const cur = xpForLevel(L), next = xpForLevel(L + 1);
  return { level: L, xp, cur, next, frac: L >= MAX_LEVEL ? 1 : (xp - cur) / (next - cur) };
}

export const TITLES: [number, string][] = [[1, 'Beginner'], [3, 'Cyclist'], [6, 'Rouleur'], [10, 'Puncheur'], [15, 'Champion'], [20, 'Legend'], [30, 'Myth']];
export const titleFor = (L: number): string => (TITLES.filter(([l]) => L >= l).pop() ?? TITLES[0])[1];

// Base looks available from level 1
export const LOOKS: Record<LookSlot, Record<string, string>> = {
  pattern: { uni: 'Plain', degrade: 'Gradient', bandes: 'Stripes', pois: 'Polka dots', damier: 'Checkerboard', arcenciel: 'Rainbow', jaune: 'Yellow jersey' },
  frame: { couleur: 'Jersey colour', noir: 'Black carbon', blanc: 'White', chrome: 'Chrome', or: 'Gold' },
  helmet: { couleur: 'Jersey colour', blanc: 'White', noir: 'Black', or: 'Gold' },
};
export const BASE_LOOKS: Record<LookSlot, string[]> = { pattern: ['uni', 'degrade'], frame: ['couleur', 'noir', 'blanc'], helmet: ['couleur', 'blanc', 'noir'] };

export const UNLOCKS: Unlock[] = [
  { level: 2, kind: 'look', slot: 'pattern', id: 'bandes', name: 'Striped jersey' },
  { level: 2, kind: 'track', id: 'coast', name: 'Track: Coast Road' },
  { level: 3, kind: 'track', id: 'nuages', name: 'Track: Cloud Pass' },
  { level: 3, kind: 'track', id: 'valley', name: 'Track: Valley Road' },
  { level: 4, kind: 'track', id: 'ardennes', name: 'Track: Ardennes Loop' },
  { level: 4, kind: 'look', slot: 'pattern', id: 'pois', name: 'Polka dot jersey' },
  { level: 4, kind: 'look', slot: 'frame', id: 'chrome', name: 'Chrome frame' },
  { level: 5, kind: 'mood', id: 'coucher', name: 'Sunset mood' },
  { level: 5, kind: 'track', id: 'forest', name: 'Track: Forest Rollers' },
  { level: 6, kind: 'track', id: 'crit', name: 'Track: City Criterium' },
  { level: 7, kind: 'look', slot: 'pattern', id: 'damier', name: 'Checkerboard jersey' },
  { level: 7, kind: 'track', id: 'summit', name: 'Track: Summit Road' },
  { level: 8, kind: 'difficulty', id: 'pro', name: 'Pro-level bots' },
  { level: 9, kind: 'look', slot: 'helmet', id: 'or', name: 'Gold helmet' },
  { level: 10, kind: 'look', slot: 'pattern', id: 'arcenciel', name: 'Rainbow jersey' },
  { level: 12, kind: 'look', slot: 'frame', id: 'or', name: 'Gold frame' },
  { level: 15, kind: 'look', slot: 'pattern', id: 'jaune', name: 'Yellow jersey' },
];

/** The level that unlocks `id`, or null when it is open from the start: the one source for locks and their labels. */
export function unlockLevel(kind: Unlock['kind'], id: string, slot?: LookSlot): number | null {
  if (kind === 'look' && slot && BASE_LOOKS[slot]?.includes(id)) return null;
  return UNLOCKS.find(x => x.kind === kind && x.id === id && (!slot || x.slot === slot))?.level ?? null;
}
export function isUnlocked(level: number, kind: Unlock['kind'], id: string, slot?: LookSlot): boolean {
  const need = unlockLevel(kind, id, slot);
  return need == null || level >= need;
}

// ---------- Achievements ----------
export const ACHIEVEMENTS: Achievement[] = [
  { id: 'first', icon: '🚲', name: 'First pedal strokes', desc: 'Finish your first activity', xp: 50, test: c => c.totals.rides >= 1 },
  { id: 'hour', icon: '⏳', name: 'An hour in the saddle', desc: 'Ride 60 minutes in one go', xp: 100, test: c => c.act.duration >= 3600 },
  { id: 'km100', icon: '💯', name: 'Centurion', desc: '100 km in total', xp: 100, test: c => c.totals.km >= 100 },
  { id: 'km500', icon: '🛣️', name: 'Big rider', desc: '500 km in total', xp: 250, test: c => c.totals.km >= 500 },
  { id: 'km1000', icon: '🌍', name: 'A thousand clicks', desc: '1,000 km in total', xp: 500, test: c => c.totals.km >= 1000 },
  { id: 'climb1000', icon: '⛰️', name: 'Climber', desc: '1,000 m of elevation in total', xp: 150, test: c => c.totals.elevation >= 1000 },
  { id: 'win', icon: '🏆', name: 'First win', desc: 'Win a Grand Prix', xp: 150, test: c => c.act.mode === 'race' && c.act.rank === 1 },
  { id: 'wins10', icon: '👑', name: 'Collector', desc: '10 wins, all modes', xp: 300, test: c => c.totals.wins >= 10 },
  { id: 'elim', icon: '🗡️', name: 'Last one standing', desc: 'Win an elimination', xp: 150, test: c => c.act.mode === 'elim' && c.act.rank === 1 },
  { id: 'online', icon: '🌐', name: 'Connected', desc: 'Finish an online race', xp: 100, test: c => c.act.type === 'race' && !!c.act.online && !!c.act.verified },
  { id: 'onlinewin', icon: '🥇', name: 'Network champion', desc: 'Win an online race', xp: 250, test: c => !!c.act.online && !!c.act.verified && c.act.rank === 1 },
  { id: 'sniper', icon: '🎯', name: 'Sniper', desc: '5 hits in a single race', xp: 120, test: c => (c.act.hits || 0) >= 5 },
  { id: 'hits50', icon: '🍌', name: 'Banana spreader', desc: '50 hits in total', xp: 200, test: c => c.totals.hits >= 50 },
  { id: 'sprint', icon: '⚡', name: 'Sprinter', desc: 'Peak above 12 W/kg', xp: 150, test: c => (c.act.maxPower || 0) / c.profile.mass >= 12 },
  { id: 'workout', icon: '📈', name: 'Disciplined', desc: 'Finish a structured workout', xp: 80, test: c => c.act.type === 'workout' && !!c.act.completed },
  { id: 'workouts10', icon: '🏋️', name: 'Dedicated', desc: '10 workouts completed', xp: 300, test: c => c.totals.workouts >= 10 },
  { id: 'test', icon: '🧪', name: 'Measured', desc: 'Do an FTP test or a Ramp test', xp: 150, test: c => !!c.act.test },
  { id: 'ghost', icon: '👻', name: 'Ghost hunter', desc: 'Beat your own time trial record', xp: 120, test: c => !!c.act.ghostRecord },
  { id: 'tss100', icon: '🔥', name: 'Big day', desc: '100 TSS in one activity', xp: 150, test: c => (c.act.TSS || 0) >= 100 },
  { id: 'streak3', icon: '📅', name: 'Consistent', desc: '3 days in a row', xp: 100, test: c => c.streak >= 3 },
  { id: 'streak7', icon: '🗓️', name: 'Unstoppable', desc: '7 days in a row', xp: 300, test: c => c.streak >= 7 },
  { id: 'plan', icon: '🎓', name: 'Graduate', desc: 'Finish a training plan', xp: 500, test: c => !!c.planCompleted },
  // legendary challenges (challenges.ts): same id as the challenge
  { id: 'everest', icon: '🏔️', name: 'Everester', desc: 'Climb 8,848 m on the road to Everest', xp: 1500, test: c => !!c.challenges?.includes('everest') },
  { id: 'observatoire', icon: '🔭', name: 'Lord of the Observatoire', desc: 'Ride 24 hours around the Royal Observatory', xp: 1500, test: c => !!c.challenges?.includes('observatoire') },
];

/**
 * Breakdown of the XP earned for an activity.
 * act : { type: 'race'|'workout', mode, rank, riders, hits, completed, test, ghostRecord, online, distance, duration, TSS }
 */
export type XpKey = 'distance' | 'effort' | 'win' | 'podium' | 'finished' | 'online' | 'hits' | 'workout' | 'test' | 'record' | 'firstOfDay' | 'game' | 'challenge';
/** `key` and `n` let the client translate the line; `label` stays the label in the source language. */
/** `ref`: identifier the line is about (the challenge of a 'challenge' line). */
export interface XpLine { key?: XpKey; n?: number; ref?: string; label: string; xp: number }
export function xpBreakdown(act: ActivityFacts, firstOfDay = false): XpLine[] {
  const out: XpLine[] = [];
  const km = act.distance / 1000, min = act.duration / 60;
  out.push({ key: 'distance', n: +km.toFixed(1), label: `${km.toFixed(1)} km ridden`, xp: Math.round(km * 8) });
  out.push({ key: 'effort', n: Math.round(min), label: `${Math.round(min)} min of effort`, xp: Math.round(min * 2) });
  if (act.type === 'race' && act.mode !== 'free' && act.mode !== 'ghost' && act.rank) {
    if (act.rank === 1) out.push({ key: 'win', label: 'Win', xp: 150 });
    else if (act.rank <= 3) out.push({ key: 'podium', n: act.rank, label: `Podium (${act.rank === 2 ? '2nd' : '3rd'})`, xp: 75 });
    else out.push({ key: 'finished', label: 'Race finished', xp: 25 });
    if (act.online && act.verified) out.push({ key: 'online', label: 'Online race', xp: 40 });
  }
  if (act.hits) out.push({ key: 'hits', n: act.hits, label: `${act.hits} hit${act.hits > 1 ? 's' : ''}`, xp: act.hits * 10 });
  if (act.type === 'workout' && act.completed) out.push({ key: 'workout', label: 'Workout finished', xp: 100 + Math.round(act.TSS || 0) });
  if (act.type === 'game' && act.completed) out.push({ key: 'game', label: 'Game finished', xp: 40 + Math.round(act.TSS || 0) });
  if (act.test) out.push({ key: 'test', label: 'FTP test', xp: 150 });
  if (act.ghostRecord) out.push({ key: 'record', n: act.time ?? 0, label: `Record beaten (${fmtTime(act.time)})`, xp: 80 });
  if (firstOfDay) out.push({ key: 'firstOfDay', label: 'First ride of the day', xp: 50 });
  return out.filter(x => x.xp > 0);
}

/** Evaluates newly unlocked achievements (those not yet in `owned`). */
export function newAchievements(ctx: AchievementCtx, owned: string[] = []): Achievement[] {
  return ACHIEVEMENTS.filter(a => !owned.includes(a.id) && a.test(ctx));
}
